import { useRef, useState, useEffect, useCallback, type ReactNode, type ComponentType } from 'react';
import { Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { haptics } from '@/lib/haptics';

/**
 * iOS Mail-style swipe-to-reveal. Drag the row left to expose Delete (and
 * optionally a secondary action like "Handle"); release past threshold to
 * snap open, tap an action to confirm, or tap the row / swipe right to close.
 */
interface SecondaryAction {
	icon: ComponentType<{ className?: string }>;
	label: string;
	onAction: () => void;
	/** Tailwind classes for the action background (default: neutral green success). */
	className?: string;
}

interface SwipeableInboxRowProps {
	children: ReactNode;
	onDelete?: () => void;
	deleteLabel?: string;
	/** Optional second reveal action, shown to the left of Delete. */
	secondary?: SecondaryAction;
	/**
	 * When true, suppresses swipe handlers entirely — used while the inbox is
	 * in bulk-selection mode so long-press / tap flow to the parent.
	 */
	disabled?: boolean;
}

const ACTION_WIDTH = 88; // px per action
const OPEN_THRESHOLD = 44;
const COMMIT_THRESHOLD = 200; // full-swipe auto-delete (only when no secondary action)

export const SwipeableInboxRow = ({
	children,
	onDelete,
	deleteLabel = 'Delete',
	secondary,
	disabled,
}: SwipeableInboxRowProps) => {
	const totalActionWidth = ACTION_WIDTH * (secondary ? 2 : 1);
	const [dx, setDx] = useState(0);
	const [open, setOpen] = useState(false);
	const [animating, setAnimating] = useState(false);
	const startX = useRef(0);
	const startY = useRef(0);
	const startDx = useRef(0);
	const locked = useRef<null | 'x' | 'y'>(null);
	const crossedHaptic = useRef(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const close = useCallback(() => {
		setAnimating(true);
		setOpen(false);
		setDx(0);
	}, []);

	useEffect(() => {
		if (!open) return;
		const handler = (e: PointerEvent) => {
			if (!containerRef.current) return;
			if (!containerRef.current.contains(e.target as Node)) close();
		};
		document.addEventListener('pointerdown', handler);
		return () => document.removeEventListener('pointerdown', handler);
	}, [open, close]);

	const anyAction = !!onDelete || !!secondary;

	const onTouchStart = (e: React.TouchEvent) => {
		if (!anyAction) return;
		const t = e.touches[0];
		startX.current = t.clientX;
		startY.current = t.clientY;
		startDx.current = open ? -totalActionWidth : 0;
		locked.current = null;
		crossedHaptic.current = false;
		setAnimating(false);
	};

	const onTouchMove = (e: React.TouchEvent) => {
		if (!anyAction) return;
		const t = e.touches[0];
		const rawDx = t.clientX - startX.current;
		const rawDy = t.clientY - startY.current;
		if (locked.current === null) {
			if (Math.abs(rawDx) < 8 && Math.abs(rawDy) < 8) return;
			locked.current = Math.abs(rawDx) > Math.abs(rawDy) ? 'x' : 'y';
		}
		if (locked.current !== 'x') return;
		let next = startDx.current + rawDx;
		if (next > 0) next = next * 0.25;
		if (next < -totalActionWidth) {
			const over = -next - totalActionWidth;
			next = -totalActionWidth - over * 0.5;
		}
		if (!crossedHaptic.current && next <= -OPEN_THRESHOLD) {
			crossedHaptic.current = true;
			haptics.tap();
		}
		setDx(next);
	};

	const onTouchEnd = () => {
		if (!anyAction) return;
		if (locked.current !== 'x') return;
		setAnimating(true);
		// Full-swipe auto-delete only when there's no secondary action, to
		// avoid ambiguity between "commit delete" and "reveal both actions".
		if (!secondary && onDelete && dx <= -COMMIT_THRESHOLD) {
			haptics.impact();
			setDx(-window.innerWidth);
			window.setTimeout(() => onDelete(), 180);
			return;
		}
		if (dx <= -OPEN_THRESHOLD) {
			setOpen(true);
			setDx(-totalActionWidth);
		} else {
			setOpen(false);
			setDx(0);
		}
	};

	const runAction = (fn: () => void) => (e: React.MouseEvent) => {
		e.stopPropagation();
		haptics.impact();
		fn();
	};

	const onCaptureClick = (e: React.MouseEvent) => {
		if (open) {
			e.stopPropagation();
			e.preventDefault();
			close();
		}
	};

	if (!anyAction || disabled) return <>{children}</>;

	const SecondaryIcon = secondary?.icon;

	return (
		<div ref={containerRef} className="relative overflow-visible rounded-2xl">
			{(dx < 0 || open) && (
				<div
					className="absolute inset-y-0 right-0 flex items-stretch overflow-hidden rounded-2xl"
					aria-hidden={!open}
				>
					{secondary && SecondaryIcon && (
						<button
							type="button"
							onClick={runAction(secondary.onAction)}
							className={cn(
								'flex flex-col items-center justify-center gap-1 px-4 h-full',
								'text-[11px] font-semibold tracking-wide',
								'active:brightness-110 transition-[filter]',
								secondary.className || 'bg-success text-success-foreground'
							)}
							style={{ width: ACTION_WIDTH }}
							tabIndex={open ? 0 : -1}
						>
							<SecondaryIcon className="h-5 w-5" />
							<span>{secondary.label}</span>
						</button>
					)}
					{onDelete && (
						<button
							type="button"
							onClick={runAction(onDelete)}
							className={cn(
								'flex flex-col items-center justify-center gap-1 px-4 h-full',
								'bg-destructive text-destructive-foreground',
								'text-[11px] font-semibold tracking-wide',
								'active:brightness-110 transition-[filter]'
							)}
							style={{
								width: secondary
									? ACTION_WIDTH
									: Math.max(ACTION_WIDTH, -dx),
							}}
							tabIndex={open ? 0 : -1}
						>
							<Trash2 className="h-5 w-5" />
							<span>{deleteLabel}</span>
						</button>
					)}
				</div>
			)}

			<div
				className="relative rounded-2xl bg-chrome"
				onTouchStart={onTouchStart}
				onTouchMove={onTouchMove}
				onTouchEnd={onTouchEnd}
				onTouchCancel={onTouchEnd}
				onClickCapture={onCaptureClick}
				style={{
					transform: `translate3d(${dx}px,0,0)`,
					transition: animating
						? 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)'
						: 'none',
				}}
				onTransitionEnd={() => setAnimating(false)}
			>
				{children}
			</div>
		</div>
	);
};

export default SwipeableInboxRow;
